// CIN-6879: the single source of truth for Reserve with Google deep links.
//
// Google publishes two URL shapes for the same landing page:
//   /<calendar_id>?location=<calendar_location_id>&service=<service_id>   (feeds)
//   /<calendar_id>~<calendar_location_id>?service=<service_id>            (the
//     composite "Merchant ID" from the entity feed pasted straight into the path)
// Both must resolve to the same pinned location, the same preselected service,
// and land the visitor on the Date & Time step.
//
// Everything here is PURE - no window, no react-router. Callers pass the query
// string. That keeps CalendarContext / SessionContext / ServiceSelection all
// agreeing by construction, which matters because the location/step decision is
// consulted from three places in SessionContext (the mismatch branch, the
// session-restore guard and the advance effect). If any of them disagreed, the
// SECOND initializeSession pass - `locations` is in its dep array, so it re-runs
// when the locations fetch lands - would revert the advance.

// Mirrors ENTITY_ID_SEPARATOR in
// backendsafe/app/integration/google_reserve/builders/base.py (make_entity_id).
export const CALENDAR_ROUTE_SEPARATOR = '~';

// Split a `<calendar_id>~<calendar_location_id>` route param into its halves.
// Only splits on the FIRST separator, and only when BOTH halves are non-empty;
// anything else is treated as a plain calendar id so the existing single-uuid
// route form is byte-for-byte unchanged.
export const parseCalendarRouteId = (routeParam) => {
  if (!routeParam) {
    return { calendarId: null, locationId: null };
  }

  const separatorIndex = routeParam.indexOf(CALENDAR_ROUTE_SEPARATOR);
  if (separatorIndex <= 0) {
    // No separator, or it is the first character (empty calendar half).
    return { calendarId: routeParam, locationId: null };
  }

  const calendarId = routeParam.slice(0, separatorIndex);
  const locationId = routeParam.slice(separatorIndex + CALENDAR_ROUTE_SEPARATOR.length);
  if (!locationId) {
    // Trailing separator: empty location half, so no split.
    return { calendarId: routeParam, locationId: null };
  }

  return { calendarId, locationId };
};

// Read the deep-linked service id from a query string. Always a string (or
// null) - service ids may be numeric in the payload, so every comparison
// against this value goes through String().
export const getDeepLinkServiceId = (search) => {
  const serviceParam = new URLSearchParams(search || '').get('service');
  return serviceParam || null;
};

// Find the deep-linked service inside a services list. Shared by the context
// advance and the ServiceSelection step-2 safety net so there is exactly ONE
// matcher (same param, same String() coercion).
export const findDeepLinkService = ({ services = [], search } = {}) => {
  const serviceParam = getDeepLinkServiceId(search);
  if (!serviceParam) return null;
  return (services || []).find((s) => String(s.id) === String(serviceParam)) || null;
};

// CIN-6784 (Issue C-2), moved here verbatim from SessionContext: resolve the
// location a bare `?service=<id>` deep link should land on - the first location
// that actually offers the service, and only when no explicit location param
// already resolved to a real location. Single-location calendars deliberately
// return null; they are owned by the auto-select effect.
//
// CIN-6879 (e2e finding #1): `locationIdParam` is the ALREADY-RESOLVED pinned
// location - CalendarContext merges `?location=` / `?locationId=` with the `~`
// path half into one value. Callers that hold it MUST pass it, otherwise the
// stand-down check below is blind to the path half and this helper hops to a
// different merchant for ~100ms before the second initializeSession pass
// corrects it. Still pure: callers that only hold the query string (and the
// unit tests that pin the CIN-6784 semantics) omit the argument and get the
// legacy re-parse.
export const findServiceDeepLinkLocation = (search, locations = [], locationIdParam) => {
  if (locations.length <= 1) {
    // Single-location calendars are owned by the auto-select effect.
    return null;
  }

  const urlParams = new URLSearchParams(search);
  const serviceParam = urlParams.get('service');
  if (!serviceParam) {
    return null;
  }

  const locationParam = locationIdParam === undefined
    ? (urlParams.get('locationId') || urlParams.get('location'))
    : locationIdParam;
  if (locationParam && locations.find(loc => loc.id === locationParam)) {
    return null;
  }

  return locations.find(loc =>
    (loc.services || []).some(service => String(service.id) === String(serviceParam))
  ) || null;
};

// Resolve where a deep link should land the visitor.
//
// Location precedence, in this exact order:
//   0. an explicit `locationIdParam` (?location= / ?locationId= / the `~` path
//      half) that RESOLVES in `locations` - the param wins even over a resumed
//      session's pin, matching the pre-existing mismatch branch in SessionContext;
//   1. otherwise a single-location calendar -> its only location (CIN-7133:
//      checked BEFORE the session stand-down, because a resumed session on a
//      single-location calendar always pins that location, which made every
//      bare `?service=` link stand down);
//   2. otherwise (MULTI-location calendars only) a `sessionLocationId` that
//      RESOLVES -> STAND DOWN. This is the `!sessionLocationResolves` term
//      CIN-6784 relies on: a genuinely resumed session beats a deep link, even
//      one the session's location can't fulfil;
//   3. otherwise the first location that offers the deep-linked service.
//
// step is 3 (Date & Time) only when BOTH a location and a service it offers
// resolved, 2 (Services) when only a location resolved, 1 (Location) otherwise.
export const resolveDeepLinkTarget = ({
  search = '',
  locations = [],
  // CIN-6879 iteration 2: deliberately NO default. `undefined` means "this
  // caller does not hold a resolved param", which keeps the legacy query
  // re-parse inside findServiceDeepLinkLocation; an explicit null/false means
  // "there is no pinned location", which must NOT fall back to the query.
  locationIdParam,
  sessionLocationId = null,
} = {}) => {
  const standDown = { location: null, service: null, step: 1 };

  const resolve = (id) => (id ? locations.find(loc => loc.id === id) || null : null);

  let location = resolve(locationIdParam);

  if (!location) {
    if (locations.length === 1) {
      location = locations[0];
    } else if (resolve(sessionLocationId)) {
      // A resumed session pins a location that exists: the deep link stands
      // down entirely and the session's own persisted step wins.
      return standDown;
    } else {
      location = findServiceDeepLinkLocation(search, locations, locationIdParam);
    }
  }

  if (!location) {
    return standDown;
  }

  const service = findDeepLinkService({ services: location.services || [], search });

  return { location, service, step: service ? 3 : 2 };
};

// CIN-7133: does a resolved deep-link target REPLACE what a resumed session
// carried? Google service links (A, then B, then C in one browser) used to keep
// showing A because the cookie session's services were restored over the link.
// True when the target lands on Date & Time and the session is not already on
// exactly that one service at that location. Booked (schedule_id) and cancelled
// (deleted_when) sessions are never replaced. A fresh session (no location, no
// services) returns true, which is the same landing a fresh deep link gets.
export const deepLinkReplacesSession = ({ target, session } = {}) => {
  if (!target || target.step !== 3 || !target.location || !target.service || !session) return false;
  if (session.schedule_id || session.deleted_when) return false; // booked / cancelled sessions are never overridden
  const ids = Array.isArray(session.services) ? session.services : [];
  const sameSelection = session.location === target.location.id
    && ids.length === 1
    && String(ids[0]) === String(target.service.id);
  return !sameSelection; // the same single service at the same location keeps its progress
};
